/**
 * AdminSupportTicketsPage — Admin-Ticketverwaltung
 *
 * Liste aller Support-Tickets (GET /api/v2/admin/support_tickets) mit
 * Status-Filter + Pagination. Klick auf eine Zeile oeffnet den Thread mit
 * Antwortformular (POST /api/v2/admin/support_tickets/:id/reply), optional
 * inkl. Schliessen des Tickets. Nur fuer Admins sichtbar (Muster aus
 * AdminUsersPage.tsx).
 */
import { Fragment, useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Pagination } from '../../components/Pagination'
import { useUserPermissions } from '../../hooks/useUserPermissions'
import {
  useAdminSupportTickets,
  useAdminSupportTicket,
  useAdminReplySupportTicket,
  type AdminSupportTicketStatus,
} from './useAdminSupportTickets'

const STATUS_FILTER_OPTIONS: Array<{ value: AdminSupportTicketStatus | undefined; key: string }> = [
  { value: undefined, key: 'all' },
  { value: 'offen', key: 'offen' },
  { value: 'geschlossen', key: 'geschlossen' },
]

function formatDate(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

function TicketDetail({ ticketId }: { ticketId: number }) {
  const { t, i18n } = useTranslation('adminSupportTickets')
  const { ticket, isLoading } = useAdminSupportTicket(ticketId)
  const [message, setMessage] = useState('')
  const [close, setClose] = useState(false)
  const reply = useAdminReplySupportTicket(ticketId)

  if (isLoading || !ticket) {
    return <p className="muted" style={{ fontSize: 13 }}>{t('loading')}</p>
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!message.trim()) return
    reply.mutate(
      { message: message.trim(), close },
      { onSuccess: () => { setMessage(''); setClose(false) } }
    )
  }

  return (
    <div data-testid={`admin-support-ticket-detail-${ticketId}`} style={{ padding: '12px 0' }}>
      {ticket.messages.map((m) => (
        <div
          key={m.id}
          className="card"
          style={{ padding: 10, marginBottom: 8, background: m.sender === 'admin' ? 'var(--surface-muted, #f5f5f5)' : undefined }}
        >
          <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
            {t(`sender.${m.sender}`)} · {formatDate(m.created_at, i18n.language)}
          </div>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>{m.body}</p>
        </div>
      ))}

      {ticket.status === 'offen' ? (
        <form onSubmit={handleSubmit} style={{ marginTop: 8 }}>
          <textarea
            className="textarea"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            placeholder={t('replyPlaceholder')}
            disabled={reply.isPending}
            data-testid="admin-support-ticket-reply-input"
          />
          <label className="row" style={{ gap: 8, fontSize: 12, marginTop: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={close}
              onChange={(e) => setClose(e.target.checked)}
              data-testid="admin-support-ticket-close-checkbox"
            />
            {t('closeTicket')}
          </label>
          <button
            type="submit"
            className="btn btn-sm btn-primary"
            disabled={reply.isPending || !message.trim()}
            style={{ marginTop: 8 }}
            data-testid="admin-support-ticket-reply-submit"
          >
            {reply.isPending ? t('replying') : t('replySubmit')}
          </button>
        </form>
      ) : (
        <p className="muted" style={{ fontSize: 12 }}>{t('closed')}</p>
      )}
    </div>
  )
}

export function AdminSupportTicketsPage() {
  const { t, i18n } = useTranslation('adminSupportTickets')
  const { isAdmin, isLoaded } = useUserPermissions()
  const [status, setStatus] = useState<AdminSupportTicketStatus | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [openId, setOpenId] = useState<number | null>(null)

  const { tickets, pagy, isLoading, isError, refetch } = useAdminSupportTickets(status, page)

  const navigateAway = useNavigate()
  useEffect(() => {
    if (isLoaded && !isAdmin) {
      navigateAway({ to: '/dashboard', replace: true })
    }
  }, [isLoaded, isAdmin, navigateAway])

  if (!isAdmin) return null

  return (
    <div className="admin-support-tickets-page" data-testid="admin-support-tickets-page">
      <div className="page-header">
        <div>
          <h1>{t('page.title')}</h1>
          <p className="muted" style={{ marginTop: 4 }}>{t('page.subtitle')}</p>
        </div>
      </div>

      <div className="filterbar">
        <div className="segment" role="group" aria-label={t('filter.ariaLabel')} data-testid="admin-support-tickets-status-filter">
          {STATUS_FILTER_OPTIONS.map(({ value, key }) => (
            <button
              key={key}
              type="button"
              className={status === value ? 'is-active' : ''}
              aria-pressed={status === value}
              onClick={() => { setStatus(value); setPage(1) }}
              data-testid={`admin-support-tickets-status-${value ?? 'all'}`}
            >
              {t(`filter.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div aria-busy="true" aria-label={t('loading')} data-testid="admin-support-tickets-loading">
          {t('loading')}
        </div>
      )}

      {isError && (
        <div className="error-state" role="alert" data-testid="admin-support-tickets-error">
          <p>{t('error.load')}</p>
          <button type="button" className="btn btn-primary" onClick={() => refetch()}>
            {t('error.retry')}
          </button>
        </div>
      )}

      {!isLoading && !isError && tickets && tickets.length === 0 && (
        <div className="empty-state" data-testid="admin-support-tickets-empty">
          <p>{t('empty.title')}</p>
        </div>
      )}

      {!isLoading && !isError && tickets && tickets.length > 0 && (
        <>
          <div className="card" style={{ overflowX: 'auto' }} data-testid="admin-support-tickets-list">
            <table className="table" aria-label={t('page.title')}>
              <thead>
                <tr>
                  <th scope="col">{t('list.colSubject')}</th>
                  <th scope="col">{t('list.colUser')}</th>
                  <th scope="col">{t('list.colStatus')}</th>
                  <th scope="col">{t('list.colCreatedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <Fragment key={ticket.id}>
                    <tr
                      data-testid={`admin-support-ticket-row-${ticket.id}`}
                      onClick={() => setOpenId(openId === ticket.id ? null : ticket.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{ticket.subject}</td>
                      <td>{ticket.user_email}</td>
                      <td>
                        <span className={ticket.status === 'offen' ? 'pill pill-success' : 'pill'}>
                          {t(`filter.${ticket.status}`)}
                        </span>
                      </td>
                      <td>{formatDate(ticket.created_at, i18n.language)}</td>
                    </tr>
                    {openId === ticket.id && (
                      <tr>
                        <td colSpan={4}>
                          <TicketDetail ticketId={ticket.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={pagy?.page ?? page}
            pages={pagy?.pages ?? 1}
            count={pagy?.count ?? tickets.length}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  )
}
